/**
 * "Review & Save Contacts" step of the Select Recipients modal.
 * Spec: Sendlinx_Recent_Recipients_Address_Book_Developer_Guide.docx (§3–§4).
 *
 * One row per unique selected address. New addresses get editable First / Last /
 * Company (blank by default: names are never derived from the email). Existing
 * contacts are marked and keep their saved fields unless the user explicitly
 * edits them; a contact with no saved name or company can be filled in directly.
 * Groups (existing + one new) and Favourites apply to every saved row.
 *
 * Saving only updates the address book. It never adds recipients to the
 * transfer and never sends anything. Cancel writes nothing.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import CloseIcon from "@material-ui/icons/Close";
import EditOutlined from "@material-ui/icons/EditOutlined";
import Undo from "@material-ui/icons/Undo";
import ContactsApi from "../../../../assets/js/utils/contacts/contactsApi";

export const NAME_MAX = 75;
export const COMPANY_MAX = 150;
export const GROUP_NAME_MAX = 120;

const lower = (v) => String(v || "").toLowerCase();
const trim = (v) => String(v || "").replace(/\s+/g, " ").trim();
const errorText = (err, fallback) => (err && err.message && err.message !== "Request Cancelled" ? err.message : fallback);
const plural = (n, one, many) => n+" "+(n === 1 ? one : (many || one+"s"));

/** Builds an editable row from a preview entry. */
const toRow = (entry) => {
    const c = entry.contact;
    let firstName = "", lastName = "", company = "";
    if(c){
        firstName = c.firstName || "";
        lastName = c.lastName || "";
        // Contacts saved before First/Last existed only have a display name: keep it whole in First name.
        if(!firstName && !lastName && c.name){ firstName = c.name; }
        company = c.company || "";
    }
    const blank = !c || (!trim(firstName) && !trim(lastName) && !trim(company));
    return {
        email: entry.email,
        status: entry.status,                // new | existing | invalid
        contact: c,
        groupIds: entry.groupIds || [],
        serverError: entry.error || null,
        firstName, lastName, company,
        saved: { firstName, lastName, company },
        // New rows and existing contacts with nothing saved are editable straight away.
        // Existing contacts with saved details need an explicit "Edit".
        editing: entry.status === "new" || (entry.status === "existing" && blank),
        explicitEdit: false
    };
};
const rowChanged = (r) => trim(r.firstName) !== trim(r.saved.firstName) || trim(r.lastName) !== trim(r.saved.lastName) || trim(r.company) !== trim(r.saved.company);
const fieldErrors = (r) => {
    const e = {};
    if(trim(r.firstName).length > NAME_MAX){ e.firstName = "Use at most "+NAME_MAX+" characters."; }
    if(trim(r.lastName).length > NAME_MAX){ e.lastName = "Use at most "+NAME_MAX+" characters."; }
    if(trim(r.company).length > COMPANY_MAX){ e.company = "Use at most "+COMPANY_MAX+" characters."; }
    return e;
};

function ReviewSaveContacts({ classes, emails, groups, onCancel, onSaved }){
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [groupList, setGroupList] = useState(groups || []);
    const [groupIds, setGroupIds] = useState([]);
    const [groupSearch, setGroupSearch] = useState("");
    const [newGroupName, setNewGroupName] = useState("");
    const [favourite, setFavourite] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [rowErrors, setRowErrors] = useState({});       // { email: { field: message } } from the server
    const [groupConflict, setGroupConflict] = useState(null); // { id, name } from a 409
    const headingRef = useRef(null);
    const emailsKey = emails.join("\n");

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(null);
        ContactsApi.previewRecent(emails).then(data => {
            if(!active){ return; }
            setRows((data.entries || []).map(toRow));
            if(data.groups){ setGroupList(data.groups); }
            setLoading(false);
        }).catch(err => {
            if(!active){ return; }
            setError(errorText(err, "The selected addresses could not be checked. Try again."));
            setLoading(false);
        });
        return () => { active = false; };
    }, [emailsKey]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { if(headingRef.current){ headingRef.current.focus(); } }, []);

    const updateRow = (email, patch) => {
        setRows(prev => prev.map(r => r.email === email ? { ...r, ...patch } : r));
        if(rowErrors[email]){ setRowErrors(prev => { const next = { ...prev }; delete next[email]; return next; }); }
    };
    const removeRow = (email) => setRows(prev => prev.filter(r => r.email !== email));

    // Local check for a clashing group name (the server checks again and answers 409).
    const localClash = useMemo(() => {
        const name = lower(trim(newGroupName));
        return name ? groupList.find(g => lower(g.name) === name) || null : null;
    }, [newGroupName, groupList]);
    const clash = groupConflict || localClash;
    const chooseExistingGroup = (g) => {
        setGroupIds(prev => (prev.includes(g.id) ? prev : prev.concat([g.id])));
        setNewGroupName("");
        setGroupConflict(null);
    };

    const valid = rows.filter(r => r.status !== "invalid");
    const newRows = valid.filter(r => r.status === "new");
    const existingRows = valid.filter(r => r.status === "existing");
    const updates = existingRows.filter(r => r.editing && rowChanged(r));
    const creatingGroup = trim(newGroupName) !== "" && !clash;
    const assignments = valid.reduce((n, r) => n + groupIds.filter(id => r.status === "new" || !r.groupIds.includes(id)).length + (creatingGroup ? 1 : 0), 0);
    const favouriteChanges = favourite ? valid.filter(r => !(r.contact && r.contact.favourite)).length : 0;
    const localErrors = valid.reduce((acc, r) => { const e = fieldErrors(r); if(Object.keys(e).length){ acc[r.email] = e; } return acc; }, {});
    const groupNameTooLong = trim(newGroupName).length > GROUP_NAME_MAX;

    let blockReason = null;
    if(loading){ blockReason = "Checking the selected addresses…"; }
    else if(valid.length === 0){ blockReason = "There are no valid addresses to save."; }
    else if(clash){ blockReason = "Choose another name for the new group, or use the existing group."; }
    else if(groupNameTooLong){ blockReason = "The group name can be at most "+GROUP_NAME_MAX+" characters."; }
    else if(Object.keys(localErrors).length){ blockReason = "Fix the highlighted fields."; }
    else if(newRows.length === 0 && updates.length === 0 && assignments === 0 && favouriteChanges === 0){
        blockReason = "These addresses are already in your contacts. Choose a group or Favourites, or edit a name, to save changes.";
    }

    const save = () => {
        if(blockReason || busy){ return; }
        setBusy(true);
        setError(null);
        setRowErrors({});
        setGroupConflict(null);
        const payload = {
            contacts: valid.map(r => ({
                email: r.email,
                firstName: trim(r.firstName),
                lastName: trim(r.lastName),
                company: trim(r.company),
                // Existing contacts change only when the user edited them.
                updateDetails: r.status === "existing" && r.editing && rowChanged(r)
            })),
            groupIds,
            newGroupName: trim(newGroupName),
            favourite
        };
        ContactsApi.bulkSave(payload).then(summary => {
            setBusy(false);
            onSaved(summary);
        }).catch(err => {
            setBusy(false);
            if(err && err.field === "newGroupName" && err.existingGroup){
                setGroupConflict(err.existingGroup);
                setGroupList(prev => (prev.some(g => g.id === err.existingGroup.id) ? prev : prev.concat([err.existingGroup])));
            }
            if(err && err.rowErrors){ setRowErrors(err.rowErrors); }
            setError(errorText(err, "The contacts could not be saved. Nothing was changed."));
        });
    };

    const saveLabel = busy ? "Saving…" : newRows.length > 0 ? "Save "+plural(newRows.length, "Contact") : "Save Changes";
    const gq = lower(groupSearch).trim();
    const visibleGroups = groupList.filter(g => !gq || lower(g.name).includes(gq) || groupIds.includes(g.id));

    const input = (r, field, label, max, errs) => (
        <td>
            <input className={classes.cellInput+" "+(errs[field] ? classes.inputInvalid : "")} value={r[field]} maxLength={max + 20}
                aria-label={label+" for "+r.email} aria-invalid={!!errs[field]} disabled={busy}
                onChange={(e) => updateRow(r.email, { [field]: e.target.value })} />
            {errs[field] ? <div className={classes.fieldError}>{errs[field]}</div> : null}
        </td>
    );

    return (<>
        <div className={classes.main}>
            <div>
                <h3 className={classes.subTitle} tabIndex={-1} ref={headingRef}>Review &amp; Save Contacts</h3>
                <p className={classes.muted} style={{ margin: "4px 0 0" }}>
                    Add names if you know them. Blank names are fine: the contact is saved by email. Nothing is added to this transfer.
                </p>
            </div>
            {error ? <div className={classes.error} role="alert">{error}</div> : null}
            {loading ? <p className={classes.empty}>Checking the selected addresses…</p> : <>
                <div className={classes.tableWrap}>
                    <table className={classes.table}>
                        <thead><tr>
                            <th scope="col">Email</th><th scope="col">First name</th><th scope="col">Last name</th><th scope="col">Company</th>
                            <th scope="col" style={{ width: 84 }}><span className={classes.srOnly}>Actions</span></th>
                        </tr></thead>
                        <tbody>
                            {rows.length === 0 ? <tr><td colSpan={5} className={classes.empty}>No addresses selected.</td></tr> : rows.map(r => {
                                const errs = { ...(localErrors[r.email] || {}), ...(rowErrors[r.email] || {}) };
                                const rowError = errs.email || (r.status === "invalid" ? r.serverError : null);
                                return (
                                    <tr key={r.email} className={rowError || Object.keys(errs).length ? classes.rowInvalid : ""}>
                                        <td>
                                            <div className={classes.emailCell}>{r.email}</div>
                                            {r.status === "new" ? <span className={classes.status+" "+classes.statusNew}>New contact</span> : null}
                                            {r.status === "existing" ? <span className={classes.status+" "+classes.statusOk}>Already in Contacts{r.editing && rowChanged(r) ? " · will update" : ""}</span> : null}
                                            {r.status === "invalid" ? <span className={classes.status+" "+classes.statusBad}>Can't be saved</span> : null}
                                            {rowError ? <div className={classes.fieldError} role="alert">{rowError}</div> : null}
                                        </td>
                                        {r.status === "invalid" ? <td colSpan={3} className={classes.muted}>Remove this row to continue with the others.</td>
                                        : r.editing ? <>
                                            {input(r, "firstName", "First name", NAME_MAX, errs)}
                                            {input(r, "lastName", "Last name", NAME_MAX, errs)}
                                            {input(r, "company", "Company", COMPANY_MAX, errs)}
                                        </> : <>
                                            <td>{r.firstName || <span className={classes.muted}>—</span>}</td>
                                            <td>{r.lastName || <span className={classes.muted}>—</span>}</td>
                                            <td>{r.company || <span className={classes.muted}>—</span>}</td>
                                        </>}
                                        <td><div className={classes.actions}>
                                            {r.status === "existing" && !r.editing ?
                                                <button type="button" className={classes.iconButton} aria-label={"Edit saved details for "+r.email} title="Edit saved details"
                                                    onClick={() => updateRow(r.email, { editing: true, explicitEdit: true })} disabled={busy}><EditOutlined /></button>
                                            : null}
                                            {r.status === "existing" && r.explicitEdit ?
                                                <button type="button" className={classes.iconButton} aria-label={"Keep saved details for "+r.email} title="Keep saved details"
                                                    onClick={() => updateRow(r.email, { ...r.saved, editing: false, explicitEdit: false })} disabled={busy}><Undo /></button>
                                            : null}
                                            <button type="button" className={classes.iconButton} aria-label={"Leave "+r.email+" out of this save"} title="Leave out of this save"
                                                onClick={() => removeRow(r.email)} disabled={busy}><CloseIcon /></button>
                                        </div></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className={classes.reviewOptions}>
                    <fieldset className={classes.fieldset}>
                        <legend className={classes.label}>Add to groups (optional)</legend>
                        <p className={classes.hint}>Applies to every address above, including ones already in your contacts.</p>
                        {groupList.length > 8 ?
                            <input type="search" className={classes.input} style={{ marginBottom: 8 }} placeholder="Search groups…" aria-label="Search groups"
                                value={groupSearch} onChange={(e) => setGroupSearch(e.target.value)} />
                        : null}
                        <div className={classes.groupList}>
                            {groupList.length === 0 ? <span className={classes.muted}>No groups yet. Create one below.</span> :
                                visibleGroups.length === 0 ? <span className={classes.muted}>No groups match.</span> : visibleGroups.map(g => (
                                    <label key={g.id} className={classes.groupPickItem}>
                                        <input type="checkbox" className={classes.checkbox} checked={groupIds.includes(g.id)} disabled={busy}
                                            onChange={(e) => { const on = e.target.checked; /* read now: React 16 pools events */ setGroupIds(prev => on ? prev.concat([g.id]) : prev.filter(id => id !== g.id)); }} />
                                        {g.name}{g.memberCount !== undefined ? <span className={classes.muted}>&nbsp;({g.memberCount})</span> : null}
                                    </label>
                                ))}
                        </div>
                    </fieldset>
                    <div className={classes.fieldset}>
                        <label htmlFor="ab-new-group" className={classes.label}>Or create a new group</label>
                        <input id="ab-new-group" className={classes.input+" "+(clash || groupNameTooLong ? classes.inputInvalid : "")} placeholder="Enter group name…"
                            value={newGroupName} maxLength={GROUP_NAME_MAX + 20} disabled={busy} aria-invalid={!!clash || groupNameTooLong}
                            aria-describedby={clash ? "ab-new-group-clash" : undefined}
                            onChange={(e) => { setNewGroupName(e.target.value); setGroupConflict(null); }} />
                        {clash ?
                            <div id="ab-new-group-clash" className={classes.fieldError} role="alert">
                                You already have a group named "{clash.name}".&nbsp;
                                <button type="button" className={classes.inlineLink} onClick={() => chooseExistingGroup(clash)}>Use that group</button> or choose another name.
                            </div>
                        : groupNameTooLong ? <div className={classes.fieldError}>Use at most {GROUP_NAME_MAX} characters.</div> : null}
                        <label className={classes.groupPickItem} style={{ marginTop: 14 }}>
                            <input type="checkbox" className={classes.checkbox} checked={favourite} disabled={busy} onChange={(e) => setFavourite(e.target.checked)} />
                            Also add to Favourites
                        </label>
                    </div>
                </div>
            </>}
        </div>
        <div className={classes.footer}>
            <div className={classes.reviewSummary} aria-live="polite">
                {loading ? null : <>
                    <strong>{plural(valid.length, "address", "addresses")}</strong>
                    <span>{plural(newRows.length, "new contact")} · {existingRows.length} already in contacts{updates.length ? " ("+updates.length+" updated)" : ""} · {plural(assignments, "group assignment")}{favouriteChanges ? " · "+favouriteChanges+" to Favourites" : ""}</span>
                    {blockReason ? <span className={classes.blockReason}>{blockReason}</span> : null}
                </>}
            </div>
            <button type="button" className={classes.secondary} onClick={onCancel} disabled={busy}>Cancel</button>
            <button type="button" className={classes.primary} onClick={save} disabled={!!blockReason || busy}>{saveLabel}</button>
        </div>
    </>);
}

ReviewSaveContacts.defaultProps = { groups: [] };
ReviewSaveContacts.propTypes = {
    classes: PropTypes.object.isRequired,
    emails: PropTypes.array.isRequired,
    groups: PropTypes.array,
    onCancel: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired
};

export default ReviewSaveContacts;
